import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { TFetchError } from '@/api/api-error.types';
import { useToast } from '@/shared/design-system';
import { getApiErrorMessage } from '@/utils/api-error-message';

import { ENTITY_ROLES_QUERY_KEYS } from '../entity-roles/queries';
import { roleRequestsClient } from './client';
import { ROLE_REQUEST_QUERY_KEYS } from './queries';
import type { RejectRoleRequestBody, RoleRequestInput } from './types';

type EntityScoped = { entityId?: string | null };

type CreateRequestOptions = EntityScoped & {
  /**
   * Skips the per-request toasts and cache refresh; a caller batching several requests reports
   * one outcome and refreshes once with `useRefreshRoleRequestCaches`.
   */
  silent?: boolean;
};

type ApiErrorShape = {
  status?: number;
  response?: { status?: number };
  data?: { statusCode?: number };
};

const isConflict = (error: unknown) => {
  const apiError = error as ApiErrorShape;
  return (apiError.response?.status ?? apiError.data?.statusCode ?? apiError.status) === 409;
};

const useRoleRequestMutation = ({ entityId }: EntityScoped) => {
  const queryClient = useQueryClient();
  const { add } = useToast();

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [ROLE_REQUEST_QUERY_KEYS.list, entityId] });
    queryClient.invalidateQueries({ queryKey: [ENTITY_ROLES_QUERY_KEYS.roles, entityId] });
    queryClient.invalidateQueries({ queryKey: [ENTITY_ROLES_QUERY_KEYS.role, entityId] });
    queryClient.invalidateQueries({ queryKey: [ENTITY_ROLES_QUERY_KEYS.roleMembers, entityId] });
    queryClient.invalidateQueries({
      queryKey: [ENTITY_ROLES_QUERY_KEYS.memberPermissions, entityId],
    });
  }, [entityId, queryClient]);

  const showError = useCallback(
    (error: unknown) => {
      add({
        title: getApiErrorMessage(error as TFetchError, 'Could not update this request.'),
        data: { status: isConflict(error) ? 'warning' : 'error' },
      });
    },
    [add],
  );
  const showSuccess = useCallback(
    (title: string) => add({ title, data: { status: 'success' } }),
    [add],
  );

  return { invalidate, showError, showSuccess, queryClient };
};

/** Refreshes everything a role request can change, for a caller that batched silent requests. */
export const useRefreshRoleRequestCaches = ({ entityId }: EntityScoped) =>
  useRoleRequestMutation({ entityId }).invalidate;

const requestResultTitle = (status: string) =>
  status === 'approved' ? 'Change applied.' : 'Request submitted for approval.';

export const useCreateRoleRequest = ({ entityId, silent = false }: CreateRequestOptions) => {
  const queryClient = useQueryClient();
  const { add } = useToast();

  return useMutation({
    mutationFn: (input: RoleRequestInput) => roleRequestsClient.create(input),
    onSuccess: (result, { payload }) => {
      if (silent) return;

      queryClient.invalidateQueries({ queryKey: [ROLE_REQUEST_QUERY_KEYS.list, entityId] });
      queryClient.invalidateQueries({ queryKey: [ENTITY_ROLES_QUERY_KEYS.roles, entityId] });

      if ('roleId' in payload) {
        queryClient.invalidateQueries({
          queryKey: [ENTITY_ROLES_QUERY_KEYS.role, entityId, payload.roleId],
        });
        queryClient.invalidateQueries({
          queryKey: [ENTITY_ROLES_QUERY_KEYS.roleMembers, entityId, payload.roleId],
        });
      }
      if ('userId' in payload) {
        queryClient.invalidateQueries({
          queryKey: [ENTITY_ROLES_QUERY_KEYS.memberPermissions, entityId, payload.userId],
        });
      }

      add({ title: requestResultTitle(result.status), data: { status: 'success' } });
    },
    onError: (error) => {
      if (silent) return;

      add({
        title: getApiErrorMessage(error as TFetchError, 'Could not submit this role change.'),
        data: { status: 'error' },
      });
    },
  });
};

export const useApproveRoleRequest = ({ entityId }: EntityScoped) => {
  const { invalidate, showError, showSuccess, queryClient } = useRoleRequestMutation({ entityId });

  return useMutation({
    mutationFn: (requestId: string) => roleRequestsClient.approve(requestId),
    onSuccess: () => {
      invalidate();
      showSuccess('Request approved.');
    },
    onError: async (error) => {
      if (isConflict(error)) {
        await queryClient.refetchQueries({
          queryKey: [ROLE_REQUEST_QUERY_KEYS.list, entityId],
          type: 'active',
        });
      }
      showError(error);
    },
  });
};

export const useRejectRoleRequest = ({ entityId }: EntityScoped) => {
  const { invalidate, showError, showSuccess, queryClient } = useRoleRequestMutation({ entityId });

  return useMutation({
    mutationFn: ({ requestId, ...body }: RejectRoleRequestBody & { requestId: string }) =>
      roleRequestsClient.reject(requestId, body),
    onSuccess: () => {
      invalidate();
      showSuccess('Request rejected.');
    },
    onError: async (error) => {
      if (isConflict(error)) {
        await queryClient.refetchQueries({
          queryKey: [ROLE_REQUEST_QUERY_KEYS.list, entityId],
          type: 'active',
        });
      }
      showError(error);
    },
  });
};
